

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API_BASE_URL from "../../src/config";
import { toast } from "react-toastify";
import { formatLocalDate, parseLocalDate } from "../utils/dateUtils";

const BannerSearch = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Get state from previous navigation
  const [urlParams] = useSearchParams();
  const locationFromUrl = urlParams.get("location"); // CTA links, e.g. /stays?location=Darjeeling
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    destination_id: "",
    destination_name: "",
    checkin_date: "",
    checkout_date: "",
    adults: "1",
    kids: "",
    rooms_required: "1",
  });


  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}home/locations`
        );
        const fetchedLocations = res.data.data || [];
        setLocations(fetchedLocations);

        // Helper for tomorrow and day-after-tomorrow
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const dayAfterTomorrow = new Date(today);
        dayAfterTomorrow.setDate(today.getDate() + 2);

        // 0️⃣ URL wins over anything cached — a CTA link landing on
        // ?location=X must show X, never a stale localStorage/state value
        // left over from an earlier unrelated search on this browser.
        if (locationFromUrl) {
          const selectedLoc = fetchedLocations.find(
            (loc) => loc.location_name.toLowerCase() === locationFromUrl.toLowerCase()
          );
          setFormData((prev) => ({
            ...prev,
            destination_id: selectedLoc?.id || "",
            destination_name: selectedLoc?.location_name || "",
            checkin_date: tomorrow,
            checkout_date: dayAfterTomorrow,
            adults: "1",
            kids: "",
            rooms_required: "1",
          }));
          setFilteredLocations(selectedLoc ? [selectedLoc] : []);
          return; // Stop — don't fall through to localStorage/state below
        }

        // 1️⃣ Check localStorage
        const storedParams = localStorage.getItem("searchParams");
        if (storedParams) {
          const params = JSON.parse(storedParams);

          // Validate and fix dates
          let checkinDate = params.checkin_date
            ? parseLocalDate(params.checkin_date)
            : tomorrow;
          let checkoutDate = params.checkout_date
            ? parseLocalDate(params.checkout_date)
            : dayAfterTomorrow;

          // If stored check-in or check-out is before today → reset
          if (checkinDate < today) checkinDate = tomorrow;
          if (checkoutDate <= checkinDate)
            checkoutDate = new Date(checkinDate.getTime() + 24 * 60 * 60 * 1000);

          setFormData((prev) => ({
            ...prev,
            destination_id: params.destination_id || "",
            destination_name: params.destination_name || "",
            checkin_date: checkinDate,
            checkout_date: checkoutDate,
            adults: params.adults || "1",
            kids: params.kids || "",
            rooms_required: params.rooms_required || "1",
          }));

          // Filter for suggestion display
          if (params.destination_name) {
            const selectedLoc = fetchedLocations.find(
              (loc) =>
                loc.location_name.toLowerCase() ===
                params.destination_name.toLowerCase()
            );
            if (selectedLoc) setFilteredLocations([selectedLoc]);
          }
          return; // Stop if localStorage found
        }

        // 2️⃣ If no localStorage, check navigation state
        if (location.state) {
          const stateData = location.state;

          let checkinDate = stateData.checkin_date
            ? parseLocalDate(stateData.checkin_date)
            : tomorrow;
          let checkoutDate = stateData.checkout_date
            ? parseLocalDate(stateData.checkout_date)
            : dayAfterTomorrow;

          // Prevent showing past dates
          if (checkinDate < today) checkinDate = tomorrow;
          if (checkoutDate <= checkinDate)
            checkoutDate = new Date(checkinDate.getTime() + 24 * 60 * 60 * 1000);

          setFormData((prev) => ({
            ...prev,
            destination_name: stateData.destination_name || "",
            destination_id: stateData.destination_id || "",
            checkin_date: checkinDate,
            checkout_date: checkoutDate,
            adults: stateData.adults || "1",
            kids: stateData.kids || "",
            rooms_required: stateData.rooms_required || "1",
          }));

          if (stateData.destination_name) {
            const selectedLoc = fetchedLocations.find(
              (loc) =>
                loc.location_name.toLowerCase() ===
                stateData.destination_name.toLowerCase()
            );
            if (selectedLoc) setFilteredLocations([selectedLoc]);
          }
        } else {
          // 3️⃣ Default case: set tomorrow + day after tomorrow
          setFormData((prev) => ({
            ...prev,
            checkin_date: tomorrow,
            checkout_date: dayAfterTomorrow,
            adults: "1",
            kids: "",
            rooms_required: "1",
          }));
        }
      } catch (err) {
        console.error("Error fetching locations:", err);
      }
    };

    fetchLocations();
  }, [location.state, locationFromUrl]);


  // Handle typing in location
  const handleLocationInput = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, destination_name: value, destination_id: "" });

    if (value.length > 0) {
      const matches = locations.filter((loc) =>
        loc.location_name.toLowerCase().startsWith(value.toLowerCase())
      );
      setFilteredLocations(matches);
    } else {
      // Show all locations (alphabetical, per the backend's own ordering)
      // when the field is empty — was previously an arbitrary top-5 slice.
      setFilteredLocations(locations);
    }
    setShowSuggestions(true);
  };

  // Handle selecting a suggestion
  const handleSelectLocation = (loc) => {
    setFormData({
      ...formData,
      destination_id: loc.id,
      destination_name: loc.location_name,
    });
    setShowSuggestions(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = () => {
    if (!formData.destination_id) {
      toast.warn("Please select a location.");
      return;
    }

    if (!formData.checkin_date || !formData.checkout_date) {
      toast.warn("Please select both check-in and check-out dates.");
      return;
    }

    // 🚫 Prevent same-day check-in and check-out
    const checkin = new Date(formData.checkin_date);
    const checkout = new Date(formData.checkout_date);
    if (checkin.toDateString() === checkout.toDateString()) {
      toast.warn("Check-in and check-out dates cannot be the same.");
      return;
    }
    const payload = {
      ...formData,
      checkin_date: formData.checkin_date
        ? formatLocalDate(formData.checkin_date)
        : "",
      checkout_date: formData.checkout_date
        ? formatLocalDate(formData.checkout_date)
        : "",
    };
    localStorage.setItem("searchParams", JSON.stringify(payload));
    navigate("/stays", { state: payload });
  };

  return (
    <section className="innerbanner">
      <div className="bannersearch">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="searchwrapper">
                <div className="bannerheading">
                  <div className="bnsubheading">Incredible India’s</div>
                  <h1>Himalayan Homestays</h1>
                </div>

                <div className="searchouter">
                  {/* Editable Location Search */}
                  <div className="searchcolumn position-relative">
                    <div className="formgroup">
                      <label htmlFor="location">Location</label>
                      <input
                        type="text"
                        id="location"
                        className="form-control"
                        placeholder="Search Location"
                        value={formData.destination_name}
                        onChange={handleLocationInput}
                        autoComplete="off"
                        title=""
                        onFocus={() => {
                          if (!formData.destination_name) {
                            setFilteredLocations(locations);
                          }
                          setShowSuggestions(true);
                        }}
                        onBlur={() =>
                          setTimeout(() => setShowSuggestions(false), 200)
                        }
                      />
                      {showSuggestions && filteredLocations.length > 0 && (
                        <ul className="suggestions-list">
                          {filteredLocations.map((loc) => (
                            <li
                              key={loc.id}
                              onClick={() => handleSelectLocation(loc)}
                            >
                              {loc.location_name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Check-in */}
                  <div className="searchcolumn">
                    <div className="formgroup">
                      <label htmlFor="checkin_date">Check in</label>
                      <DatePicker
                        selected={formData.checkin_date}
                        // onChange={(date) =>
                        //   setFormData({ ...formData, checkin_date: date })
                        // }
                        onChange={(date) => {
                          const nextDay = new Date(date);
                          nextDay.setDate(date.getDate() + 1);
                          setFormData({
                            ...formData,
                            checkin_date: date,
                            checkout_date:
                              !formData.checkout_date || formData.checkout_date <= date
                                ? nextDay
                                : formData.checkout_date,
                          });
                        }}

                        selectsStart
                        startDate={formData.checkin_date}
                        endDate={formData.checkout_date}
                        placeholderText="Select check-in"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        minDate={new Date()}
                      />
                    </div>
                  </div>

                  {/* Check-out */}
                  <div className="searchcolumn">
                    <div className="formgroup">
                      <label htmlFor="checkout_date">Check out</label>
                      {/* <DatePicker
                        selected={formData.checkout_date}
                        onChange={(date) =>
                          setFormData({ ...formData, checkout_date: date })
                        }
                        selectsEnd
                        startDate={formData.checkin_date}
                        endDate={formData.checkout_date}
                        minDate={formData.checkin_date || new Date()}
                        placeholderText="Select check-out"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                      /> */}

                      <DatePicker
                        selected={formData.checkout_date}
                        onChange={(date) =>
                          setFormData({ ...formData, checkout_date: date })
                        }
                        selectsEnd
                        startDate={formData.checkin_date}
                        endDate={formData.checkout_date}
                        minDate={
                          formData.checkin_date
                            ? new Date(new Date(formData.checkin_date).getTime() + 24 * 60 * 60 * 1000)
                            : new Date()
                        }
                        placeholderText="Select check-out"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                      />

                    </div>
                  </div>


                  <div className="searchcolumn d-flex gap-2">
                    {/* Rooms */}
                    <div className="formgroup flex-fill">
                      <label htmlFor="rooms_required">Rooms</label>
                      <select
                        id="rooms_required"
                        className="form-select"
                        value={formData.rooms_required}
                        onChange={handleChange}
                        style={{ padding: "0 3px" }}
                      >


                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i + 1} >
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {/* Guests: Adults & Kids */}
                  <div className="searchcolumn d-flex gap-2">
                    {/* Adults */}
                    <div className="formgroup flex-fill">
                      <label htmlFor="adults">Adults</label>
                      <select
                        id="adults"
                        className="form-select"
                        value={formData.adults}
                        onChange={handleChange}
                        style={{ padding: "0 3px" }}
                      >


                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i + 1} >
                            {i + 1}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Kids */}
                    <div className="formgroup flex-fill">
                      <label htmlFor="kids">Kids</label>
                      <select
                        id="kids"
                        className="form-select"
                        value={formData.kids}
                        onChange={handleChange}
                        style={{ padding: "0 3px" }}

                      >

                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i}>
                            {i}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Search Button */}
                  <button
                    className="theme-btn"
                    type="button"
                    onClick={handleSubmit}
                  >
                    <i className="icon icon-search" />
                  </button>
                </div>

                {/* CSS for suggestions dropdown */}
                <style>{`
                  .suggestions-list {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 9;
                    background: #fff;
                    border: 1px solid #ddd;
                    max-height: 200px;
                    overflow-y: auto;
                    list-style: none;
                    margin: 0;
                    padding: 0;
                  }
                  .suggestions-list li {
                    padding: 8px 12px;
                    cursor: pointer;
                  }
                  .suggestions-list li:hover {
                    background: #f5f5f5;
                  }
                `}</style>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BannerSearch;
